import {Label} from '@components/ui/label/Label.tsx'
import {useTranslation} from '@/translations/i18n'
import {useController, useFormContext, useWatch} from 'react-hook-form'
import {ProjectFieldsSchema} from '@/features/projects/formSchemas'
import {z} from 'zod'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Root} from '@radix-ui/react-toggle-group'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {RadioBoxItem} from '@/components/ui/radio-box-atoms/RadioBoxAtoms'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'

const ProjectSectorFieldSchema = ProjectFieldsSchema.pick({
    sector: true,
    assistiveRegime: true,
    patient: true
})
type ProjectSectorFieldSchema = z.infer<typeof ProjectSectorFieldSchema>

export const ProjectSectorField = ({hideRequiredLabel}: {hideRequiredLabel?: boolean}) => {
    const {t} = useTranslation()

    const {control, setValue, resetField} = useFormContext<ProjectSectorFieldSchema>()
    const {field, fieldState} = useController({control, name: 'sector'})
    const serviceType = useWatch({control, name: 'patient.serviceType'})

    const handleChange = (value: ProjectSectorFieldSchema['sector']) => {
        if (!value) return

        if ((value == 'outpatient' || value == 'mixed') && serviceType == 'evolutionary_age') {
            setValue('assistiveRegime', 'autism')
        } else {
            resetField('assistiveRegime')
        }

        field.onChange(value)
    }

    return (
        <Flexbox direction="column" gap={1.5} fullWidth>
            <Label>
                {t('commons:intervention_mode')}
                {` `}
                {!hideRequiredLabel && <span className="description">{t('errors:required')}</span>}
            </Label>
            <Root type="single" value={field.value || null} onValueChange={handleChange} style={{width: '100%'}}>
                <Grid columns={3} gap={4} fullWidth>
                    <GridItem gap={1} fullWidth>
                        <RadioBoxItem name={field.name} value="outpatient" error={!!fieldState.error?.message}>
                            {t('commons:outpatient')}
                        </RadioBoxItem>
                    </GridItem>
                    <GridItem gap={1} fullWidth>
                        <RadioBoxItem name={field.name} value="domiciliary" error={!!fieldState.error?.message}>
                            {t('commons:domiciliary')}
                        </RadioBoxItem>
                    </GridItem>
                    <GridItem gap={1} fullWidth>
                        <RadioBoxItem name={field.name} value="mixed" error={!!fieldState.error?.message}>
                            {t('commons:mixed')}
                        </RadioBoxItem>
                    </GridItem>
                </Grid>
            </Root>
            {fieldState.error?.message && <InputHelpText error={fieldState.error?.message} />}
        </Flexbox>
    )
}
